import React, { useEffect, useState, useRef } from 'react';
import { FiShield, FiShoppingBag, FiChevronDown } from 'react-icons/fi';
import { Link } from 'react-router-dom';

// Particle animation component
const ParticleBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set canvas dimensions
    const resizeCanvas = () => {
      if (!canvas) return;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
    
    // Particle properties
    const particleCount = 50;
    const particles: {
      x: number;
      y: number;
      size: number;
      speedX: number;
      speedY: number;
      opacity: number;
      color: string;
    }[] = [];
    
    // Create particles
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 2 + 0.5,
        speedX: Math.random() * 0.5 - 0.25,
        speedY: Math.random() * 0.5 - 0.25,
        opacity: Math.random() * 0.5 + 0.1,
        color: '#9333ea' // Accent color
      });
    }
    
    // Animation loop
    const animate = () => {
      if (!canvas || !ctx) return;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach(particle => {
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = particle.color + Math.floor(particle.opacity * 255).toString(16).padStart(2, '0');
        ctx.fill();
        
        // Update position
        particle.x += particle.speedX;
        particle.y += particle.speedY;
        
        // Bounce off edges
        if (particle.x < 0 || particle.x > canvas.width) {
          particle.speedX *= -1;
        }
        
        if (particle.y < 0 || particle.y > canvas.height) {
          particle.speedY *= -1;
        }
      });
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);
  
  return (
    <canvas 
      ref={canvasRef} 
      className="absolute inset-0 z-1"
      style={{ pointerEvents: 'none' }}
    ></canvas>
  );
};

const Hero: React.FC = () => {
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  
  // Track scroll position for parallax effect
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      setIsVisible(window.scrollY < 200);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to the featured section
  const scrollToFeatured = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    
    const featuredSection = document.getElementById('featured');
    if (featuredSection) {
      // Get viewport width to apply different offsets for mobile/desktop
      const viewportWidth = window.innerWidth;
      
      // Different offset for mobile vs desktop
      const headerOffset = viewportWidth < 768 ? 60 : 80;
      
      // Calculate position with offset
      const elementPosition = featuredSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      // Smooth scroll to the featured section
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      
      // Update URL hash for proper deep linking
      window.history.pushState(null, '', '#featured');
    }
  };

  return (
    <div className="relative w-full h-[90vh] min-h-[600px] overflow-hidden">
      {/* Parallax Background */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: "url('/white-lobster.jpg')",
          backgroundSize: "65%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          transform: `translateY(${scrollY * 0.3}px)`,
          transition: 'transform 0.2s cubic-bezier(0.17, 0.67, 0.83, 0.67)'
        }}
      ></div>
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-background/60"></div>
      
      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Decorative Elements */}
        <div 
          className="absolute top-[15%] left-[10%] w-64 h-64 rounded-full bg-accent/5 blur-3xl animate-float"
          style={{ 
            transform: `translateY(${scrollY * 0.2}px)`,
            animationDelay: '0.5s' 
          }}
        ></div>
        <div 
          className="absolute bottom-[20%] right-[15%] w-80 h-80 rounded-full bg-accent/10 blur-3xl animate-float"
          style={{ 
            transform: `translateY(${scrollY * -0.15}px)`,
            animationDelay: '0.8s' 
          }}
        ></div>
        
        {/* Subtle Shapes */}
        <div 
          className="absolute top-[30%] right-[30%] w-20 h-20 border border-accent/20 rounded-md animate-spin-slow animate-float"
          style={{ 
            transform: `translateY(${scrollY * -0.1}px) rotate(${scrollY * 0.05}deg)` 
          }}
        ></div>
        <div 
          className="absolute bottom-[35%] left-[25%] w-16 h-16 border border-white/10 rounded-full animate-spin-slow animate-float"
          style={{ 
            transform: `translateY(${scrollY * 0.12}px) rotate(${scrollY * -0.03}deg)` 
          }}
        ></div>
      </div>
      
      {/* Content */}
      <div className="container mx-auto px-4 h-full relative z-10 flex items-center">
        <div className="max-w-3xl">
          {/* Updated layout: Logo above heading */}
          <div className="flex flex-col items-start gap-6 mb-6">
            {/* Logo - removed as requested */}
            
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight animate-slide-up opacity-0" style={{ animationDelay: '0.2s' }}>
                Discover Authentic <br />
                <span className="text-gradient">Grail Sneakers</span> 
              </h1>
              <p className="text-lg md:text-xl text-secondary mt-4 max-w-xl animate-slide-up opacity-0" style={{ animationDelay: '0.3s' }}>
                Limited edition, rare, and exclusive sneakers from the most sought-after collaborations and releases.
              </p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-4 animate-slide-up opacity-0" style={{ animationDelay: '0.4s' }}>
            <a 
              href="/#featured" 
              onClick={scrollToFeatured}
              className="px-8 py-3 bg-accent text-white font-medium rounded-lg hover:bg-accent/90 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg hover:shadow-accent/20"
            >
              Shop Now
            </a>
          </div>
          
          <div className="mt-12 flex items-center gap-6 animate-fade-in opacity-0" style={{ animationDelay: '0.6s' }}>
            <div className="text-sm text-secondary">
              Join <span className="text-accent font-medium">8,000+</span> customers who trust us
            </div>
          </div>
        </div>
      </div>
      
      {/* Scroll indicator */}
      <div 
        className={`absolute bottom-10 left-0 right-0 flex justify-center transition-opacity duration-500 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <button 
          onClick={scrollToFeatured}
          className="animate-bounce flex flex-col items-center text-white/70 hover:text-white transition-colors"
        >
          <span className="text-sm mb-2">Scroll to explore</span>
          <FiChevronDown className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
};

export default Hero; 